import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { API } from "./shared";

const FIELDS = [["title", "Title"], ["quantity", "Quantity", "number"], ["dimensions", "Dimensions"], ["stock", "Stock"],
  ["finishes", "Finishes"], ["ship_to", "Ship to"], ["price_floor", "Price floor (INR)", "number"], ["substrate", "Substrate"], ["post_press", "Required finishes (comma-separated)"]];
const ibox = { width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "7px 9px", fontSize: 13, fontFamily: "inherit", marginTop: 3 };

const initial = (job) => {
  const rc = job.required_capabilities || {};
  return { title: job.title || "", quantity: job.quantity ?? "", dimensions: job.dimensions || "", stock: job.stock || "", finishes: job.finishes || "",
    ship_to: job.ship_to || "", price_floor: job.price_floor ?? 0, substrate: rc.substrate || "", post_press: (rc.post_press || []).join(", "),
    press_type: rc.press_type || "", product_type: rc.product_type || "" };
};

export function LiveSpecEditor({ job, refresh, close }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => initial(job));
  const [ref, setRef] = useState({ press_types: [], product_capabilities: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) axios.get(`${API}/capabilities/reference`).then(r => setRef(r.data)).catch(() => {}); }, [open]);
  const save = async () => {
    const base = initial(job), body = {};
    Object.keys(form).forEach(k => {
      if (String(form[k]) === String(base[k])) return;
      if (k === "post_press") body[k] = form[k].split(",").map(s => s.trim()).filter(Boolean);
      else if (["quantity", "price_floor"].includes(k)) body[k] = Number(form[k]);
      else body[k] = form[k].trim() || null;
    });
    if (!Object.keys(body).length) { toast.info("Nothing changed"); return; }
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/jobs/${job.id}/specs`, body);
      toast.success(`Specs updated (${r.data.changes.length} change${r.data.changes.length > 1 ? "s" : ""})${r.data.extended_24h ? " · bidding extended 24h" : ""}. Buyer notified.`);
      close(); await refresh();
    } catch (e) { const d = e.response?.data?.detail; toast.error(Array.isArray(d) ? `Please check ${d[0]?.loc?.slice(-1)[0]}` : d || "Could not save"); }
    finally { setBusy(false); }
  };
  if (!open) return <button data-testid="job-edit-specs-btn" className="so-btn so-blue" onClick={() => setOpen(true)}><Pencil size={14}/>Edit specs</button>;
  return (
    <div data-testid="job-edit-specs-form" style={{ width: "100%", marginTop: 6 }}>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>Allowed only while there are no bids. If under 24 hours are left, bidding gets 24 more hours. The buyer is told what changed.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {FIELDS.map(([k, label, type]) => (
          <label key={k} style={{ fontSize: 11.5, fontWeight: 600, color: "#475569" }}>{label}
            <input data-testid={`spec-input-${k}`} type={type || "text"} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} style={ibox}/>
          </label>
        ))}
        {[["press_type", "Press type", ref.press_types], ["product_type", "Product type", ref.product_capabilities || []]].map(([k, label, opts]) => (
          <label key={k} style={{ fontSize: 11.5, fontWeight: 600, color: "#475569" }}>{label}
            <select data-testid={`spec-input-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} style={ibox}>
              <option value="">Any</option>{opts.map(o => <option key={o}>{o}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button data-testid="job-edit-specs-save" className="so-btn so-blue" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
        <button data-testid="job-edit-specs-cancel" className="so-btn" onClick={() => { setForm(initial(job)); setOpen(false); }}>Cancel</button>
      </div>
    </div>
  );
}
